import { useId, useState, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/toast';
import { RESPONSIBILITY_ROLE_LABELS } from '@/features/document-dossier/types';
import { PersonPicker } from '@/features/people/components/PersonPicker';
import type { PersonSelection } from '@/features/people/types';
import { useAddFromLms } from '@/features/people/use-people';

import { assignedToast, responsibilityError } from '../presentation';
import { RESPONSIBILITY_ROLES, type Position, type ResponsibilityRole } from '../types';
import { useAssignResponsibility } from '../use-responsibilities';
import { PositionField } from './PositionField';

// "Asignar responsable" (spec 2026-10-14 §1): el rol y una persona (buscador único de
// personas, sin invitaciones: un responsable es de la organización) o un puesto del
// LMS. Una persona del LMS que aún no está en el QMS se agrega antes de asignarla.

type Mode = 'person' | 'position';
type Chosen = Extract<PersonSelection, { kind: 'qms' | 'lms' }>;

const ROLE_OPTIONS = RESPONSIBILITY_ROLES.map((r) => ({
  value: r,
  label: RESPONSIBILITY_ROLE_LABELS[r],
}));

export function AssignResponsibilityModal({
  entryId,
  code,
  role: initialRole,
  onClose,
}: {
  entryId: string;
  code: string;
  role: ResponsibilityRole;
  onClose: () => void;
}): ReactElement {
  const roleId = useId();
  const assign = useAssignResponsibility(entryId);
  const addFromLms = useAddFromLms();
  const { showToast } = useToast();
  const [role, setRole] = useState<ResponsibilityRole>(initialRole);
  const [mode, setMode] = useState<Mode>('person');
  const [person, setPerson] = useState<Chosen | null>(null);
  const [position, setPosition] = useState<Position | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const chosenName = mode === 'person' ? person?.name : position?.name;

  function switchMode(next: Mode): void {
    setMode(next);
    setPerson(null);
    setPosition(null);
    setError(null);
  }

  function pickPerson(s: PersonSelection): void {
    // Sin invitaciones (allowInvite=false) el buscador solo devuelve personas.
    if (s.kind !== 'invite') {
      setPerson(s);
    }
  }

  async function confirm(): Promise<void> {
    if (busy || chosenName === undefined) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (mode === 'position' && position !== null) {
        await assign.mutateAsync({ role, positionId: position.id });
      } else if (person !== null) {
        const userId =
          person.kind === 'qms'
            ? person.userId
            : await addFromLms.mutateAsync({ authSubject: person.authSubject });
        await assign.mutateAsync({ role, userId });
      }
      showToast(assignedToast(chosenName, role, code));
      onClose();
    } catch (err) {
      setError(responsibilityError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell
      open
      title="Asignar responsable"
      wide
      busy={busy}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn mut sm" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn sm"
            onClick={() => void confirm()}
            disabled={chosenName === undefined || busy}
          >
            {busy ? 'Asignando…' : 'Asignar'}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="fld">
          <label htmlFor={roleId} className="fld-lab">
            Rol
          </label>
          <Select
            id={roleId}
            value={role}
            options={ROLE_OPTIONS}
            onChange={(v) => setRole(v as ResponsibilityRole)}
          />
        </div>
        <div className="fld">
          <span className="fld-lab">Asignar a</span>
          <div className="seg" role="group" aria-label="Asignar a">
            {(['person', 'position'] as const).map((m) => (
              <button
                key={m}
                type="button"
                className={mode === m ? 'on' : undefined}
                aria-pressed={mode === m}
                onClick={() => switchMode(m)}
              >
                {m === 'person' ? 'Persona' : 'Puesto'}
              </button>
            ))}
          </div>
        </div>
        {mode === 'position' ? (
          <PositionField selected={position} onSelect={setPosition} />
        ) : person === null ? (
          <PersonPicker allowInvite={false} allowExternal={false} onSelect={pickPerson} />
        ) : (
          <div className="fld">
            <span className="fld-lab">Persona</span>
            <div className="person-chosen">
              <strong>{person.name}</strong>
              <span className="soft">
                {person.kind === 'qms' ? 'En el QMS' : 'Del LMS: se agregará al QMS al asignar'}
              </span>
              <button type="button" className="btn mut sm" onClick={() => setPerson(null)}>
                Cambiar
              </button>
            </div>
          </div>
        )}
        {error && (
          <p className="fld-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </ModalShell>
  );
}
